import { Select, TextInput } from '@octano/global-ui';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import {
  useFilteredCities,
  useFilteredCommunes,
} from '../../../hooks/useFilteredCommunes';
import { useValidations } from '../../../hooks/useValidations';
import { formatOptions } from '../../../utils/selectFormat';
import { useAsyncOptionsFieldsLoader } from '../../../views/StudentFileAsyncOptions/AsyncOptionsFieldsLoader';
import { SectionTitle } from '../../text';
import { DynamicContactType } from './RenderSections';

type ContactReformadaSectionProps = {
  prefix: string;
  type: DynamicContactType;
  optionalFields?: string[];
  fieldNames?: {
    addressStreet: string;
    addressNumber: string;
    department: string;
    region: string;
    commune: string;
    phone: string;
    cellPhone: string;
    email: string;
    neighborhood: string;
    zone: string;
  };
  disabled?: boolean;
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DynamicContactReformada = ({
  prefix,
  type,
  optionalFields = [],
  fieldNames = {
    addressStreet: 'contactInformationDuringAcademicPeriod_residenceAddress',
    addressNumber:
      'contactInformationDuringAcademicPeriod_residenceAddressExtra',
    department: 'contactInformationDuringAcademicPeriod_department',
    region: 'contactInformationDuringAcademicPeriod_city',
    commune: 'contactInformationDuringAcademicPeriod_municipality',
    phone: 'contactInformationDuringAcademicPeriod_phone',
    cellPhone: 'contactInformationDuringAcademicPeriod_cellphone',
    email: 'contactInformationDuringAcademicPeriod_email',
    neighborhood: 'contactInformationDuringAcademicPeriod_neighborhood',
    zone: 'contactInformationDuringAcademicPeriod_zone',
  },
  disabled = false,
}: ContactReformadaSectionProps) => {
  const { t } = useTranslation();
  const { control, watch, formState, setValue } = useFormContext();
  const { validateTextNotEmpty, validatePhone } = useValidations();
  const {
    data: { regions, areaTypes, communes, cities } = {
      data: [],
    },
  } = useAsyncOptionsFieldsLoader();

  const [selectedDepartment] = watch([fieldNames.department]);

  const memoRegios = useMemo(
    () =>
      (cities ?? []).map((c) => ({
        value: c.id,
        label: c.name,
        regionId: c.regionId,
      })),
    [cities],
  );
  const filteredRegions = useFilteredCommunes(selectedDepartment, memoRegios);

  const memoComunes = useMemo(
    () =>
      (communes ?? []).map((c) => ({
        value: c.id,
        label: c.name,
        regionId: c.regionId,
      })),
    [communes],
  );
  const filteredCommunes = useFilteredCities(selectedDepartment, memoComunes);

  const isDeparmentTouched = formState.touchedFields[fieldNames.department];

  const optional = useCallback(
    (value: string, fieldName: string) => {
      if (optionalFields.includes(fieldName)) {
        return undefined;
      }
      return validateTextNotEmpty(value);
    },
    [optionalFields, validateTextNotEmpty],
  );

  useEffect(() => {
    if (isDeparmentTouched) {
      setValue(fieldNames.region, null);
      setValue(fieldNames.commune, null);
    }
  }, [
    selectedDepartment,
    setValue,
    isDeparmentTouched,
    fieldNames.region,
    fieldNames.commune,
  ]);

  // Memo
  const areaTypesOptions = useMemo(() => {
    return formatOptions(areaTypes ?? []);
  }, [areaTypes]);

  const departmentOptions = useMemo(() => {
    return formatOptions(regions ?? []);
  }, [regions]);

  const fields = useMemo(() => {
    return (
      <Fragment>
        <Row className="pt-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row>
          <Col className="pb-3" xs={12} lg={8}>
            <TextInput
              name={fieldNames.addressStreet}
              label={t(`${prefix}.${fieldNames.addressStreet}`)}
              control={control}
              rules={{
                validate: {
                  require: (value) => optional(value, fieldNames.addressStreet),
                },
              }}
              readOnly={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.addressNumber}
              label={t(`${prefix}.${fieldNames.addressNumber}`)}
              control={control}
              rules={{
                validate: {
                  require: (value) => optional(value, fieldNames.addressNumber),
                },
              }}
              readOnly={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            {/* Departamento */}
            <Select
              name={fieldNames.department}
              label={t(`${prefix}.${fieldNames.department}`)}
              options={departmentOptions}
              control={control}
              rules={{
                validate: {
                  require: (value) => optional(value, fieldNames.department),
                },
              }}
              disabled={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            {/* Ciudad */}
            <Select
              name={fieldNames.region}
              label={t(`${prefix}.${fieldNames.region}`)}
              options={filteredRegions}
              control={control}
              rules={{
                validate: {
                  require: (value) => {
                    return optional(value, fieldNames.region);
                  },
                },
              }}
              shouldUnregister
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            {/* Municipio */}
            <Select
              name={fieldNames.commune}
              label={t(`${prefix}.${fieldNames.commune}`)}
              options={filteredCommunes}
              control={control}
              rules={{
                validate: {
                  require: (value) => optional(value, fieldNames.commune),
                },
              }}
              shouldUnregister
              disabled={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.phone}
              label={t(`${prefix}.${fieldNames.phone}`)}
              control={control}
              formatter="phone"
              rules={{ validate: validatePhone }}
              placeholder={t(`common.placeholder.phone`)}
              readOnly={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.cellPhone}
              label={t(`${prefix}.${fieldNames.cellPhone}`)}
              control={control}
              formatter="phone"
              rules={{
                validate: {
                  phone: validatePhone,
                  require: (value) => optional(value, fieldNames.cellPhone),
                },
              }}
              placeholder={t(`common.placeholder.cellphone`)}
              readOnly={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={`${fieldNames.email}`}
              label={t(`${prefix}.${fieldNames.email}`)}
              control={control}
              rules={{ validate: validateTextNotEmpty }}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={`${fieldNames.neighborhood}`}
              label={t(`${prefix}.${fieldNames.neighborhood}`)}
              control={control}
              rules={{ validate: validateTextNotEmpty }}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.zone}
              label={t(`${prefix}.${fieldNames.zone}`)}
              options={areaTypesOptions}
              control={control}
              rules={{
                validate: {
                  require: (value) => optional(value, fieldNames.zone),
                },
              }}
            />
          </Col>
        </Row>
      </Fragment>
    );
  }, [
    disabled,
    fieldNames.addressNumber,
    fieldNames.addressStreet,
    fieldNames.department,
    fieldNames.region,
    fieldNames.commune,
    fieldNames.cellPhone,
    fieldNames.phone,
    fieldNames.email,
    fieldNames.neighborhood,
    fieldNames.zone,
    optional,
    departmentOptions,
    areaTypesOptions,
    t,
    validatePhone,
    validateTextNotEmpty,
    control,
    filteredRegions,
    filteredCommunes,
    prefix,
  ]);

  return <>{fields}</>;
};

export default DynamicContactReformada;
